/**
 * Authentication Store.
 *
 * Global auth state managed by Zustand. Persists the JWT token in
 * localStorage and provides actions for login, logout, and loading
 * the current user profile.
 */

import { create } from "zustand";
import { apiClient } from "@/api/client";
import type { LoginRequest, TokenResponse, User } from "@/types/user";

interface AuthState {
  /** The currently authenticated user, or null if not logged in. */
  user: User | null;
  /** The JWT access token, or null if not authenticated. */
  token: string | null;
  /** Whether an auth operation (login, fetchUser) is in progress. */
  isLoading: boolean;
  /** Whether the initial auth check has completed. */
  isInitialized: boolean;

  /** Authenticate with email/password and store the token. */
  login: (credentials: LoginRequest) => Promise<void>;
  /** Clear credentials and redirect to login. */
  logout: () => void;
  /**
   * Swap in a token the server issued without a sign-in — the one a password
   * change returns. A change signs out every earlier token, this one included.
   */
  adoptToken: (token: string) => void;
  /** Fetch the current user profile from the API using the stored token. */
  fetchUser: () => Promise<void>;
  /** Initialize auth state from localStorage on app mount. */
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem("access_token"),
  isLoading: false,
  isInitialized: false,

  login: async (credentials: LoginRequest) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post<TokenResponse>(
        "/api/v1/auth/login",
        credentials,
      );
      const { access_token } = response.data;

      localStorage.setItem("access_token", access_token);
      set({ token: access_token });

      // Fetch user profile after obtaining the token.
      await get().fetchUser();
    } finally {
      set({ isLoading: false });
    }
  },

  logout: () => {
    localStorage.removeItem("access_token");
    set({ user: null, token: null });
  },

  adoptToken: (token: string) => {
    localStorage.setItem("access_token", token);
    set({ token });
  },

  fetchUser: async () => {
    const { token } = get();
    if (!token) {
      set({ user: null, isInitialized: true });
      return;
    }

    try {
      const response = await apiClient.get<User>("/api/v1/auth/me");
      set({ user: response.data, isInitialized: true });
    } catch {
      // Token is invalid or expired — clear state.
      localStorage.removeItem("access_token");
      set({ user: null, token: null, isInitialized: true });
    }
  },

  initialize: async () => {
    const { token } = get();
    if (token) {
      set({ isLoading: true });
      try {
        await get().fetchUser();
      } finally {
        set({ isLoading: false });
      }
    } else {
      set({ isInitialized: true });
    }
  },
}));
